import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowUpDown, ArrowUp, ArrowDown, Eye, FileJson, FlaskConical, Loader2 } from 'lucide-react';

function getVal(payload, field) {
  if (!payload || !field) return '';
  return payload[field] !== undefined ? String(payload[field]) : '';
}

function SortIcon({ field, sortField, sortOrder }) {
  if (field !== sortField) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
  return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
}

export default function ZohoReportTable({ submissions, columns, sortField, sortOrder, onSort, form, onRefresh }) {
  const [viewSub, setViewSub] = useState(null);
  const [toggling, setToggling] = useState(null);

  const toggleTest = async (sub) => {
    setToggling(sub.id);
    try {
      const res = await zohoAPI.toggleSubmissionTest(sub.id);
      toast.success(res.data.message);
      onRefresh();
      setViewSub(null);
    } catch { toast.error('Failed to update'); }
    setToggling(null);
  };

  if (submissions.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground" data-testid="empty-report">
          <FileJson className="h-10 w-10 mx-auto mb-2 opacity-20" />
          <p className="font-medium">No records found</p>
          <p className="text-sm mt-1">Adjust your filters or wait for submissions</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">#</TableHead>
                  {columns.map(col => (
                    <TableHead
                      key={col}
                      className="min-w-[120px] cursor-pointer select-none hover:bg-muted/50"
                      onClick={() => onSort(col)}
                    >
                      <span className="flex items-center gap-1">
                        {col}
                        <SortIcon field={col} sortField={sortField} sortOrder={sortOrder} />
                      </span>
                    </TableHead>
                  ))}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center w-[60px]">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((s, idx) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setViewSub(s)}
                    data-testid={`report-row-${s.id}`}
                  >
                    <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                    {columns.map(col => (
                      <TableCell key={col} className="text-sm max-w-[200px] truncate" title={getVal(s.raw_payload, col)}>
                        {getVal(s.raw_payload, col) || '—'}
                      </TableCell>
                    ))}
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge variant={s.processing_status === 'success' ? 'default' : 'secondary'} className="text-xs">
                          {s.processing_status}
                        </Badge>
                        {s.is_test && <Badge variant="outline" className="text-xs gap-0.5"><FlaskConical className="h-2.5 w-2.5" />Test</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setViewSub(s)}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!viewSub} onOpenChange={(val) => !val && setViewSub(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="report-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Submission Details
              {viewSub?.is_test && <Badge variant="outline" className="text-xs gap-0.5"><FlaskConical className="h-2.5 w-2.5" />Test</Badge>}
            </DialogTitle>
          </DialogHeader>
          {viewSub && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">ID</Label>
                  <p className="font-mono text-xs mt-0.5">{viewSub.submission_id || viewSub.id}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <p className="mt-0.5"><Badge variant="default" className="text-xs">{viewSub.processing_status}</Badge></p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Received</Label>
                  <p className="text-xs mt-0.5">{viewSub.created_at ? new Date(viewSub.created_at).toLocaleString() : '—'}</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">All Fields</Label>
                <div className="border rounded-md mt-1 divide-y max-h-[400px] overflow-auto">
                  {Object.entries(viewSub.raw_payload || {}).map(([key, val]) => (
                    <div key={key} className="flex items-start gap-3 px-3 py-2 text-sm">
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded min-w-[140px] shrink-0 text-muted-foreground">{key}</span>
                      <span className="flex-1 break-all">{typeof val === 'object' ? JSON.stringify(val) : String(val ?? '—')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-row justify-between sm:justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => viewSub && toggleTest(viewSub)}
              disabled={toggling === viewSub?.id}
              data-testid="report-toggle-test-btn"
              className="gap-1"
            >
              {toggling === viewSub?.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <FlaskConical className="h-3.5 w-3.5" />
              {viewSub?.is_test ? 'Unmark as Test' : 'Mark as Test'}
            </Button>
            <Button variant="outline" onClick={() => setViewSub(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
